import { useState, useEffect, useRef } from "react";
import "./Wazawan.css";
import { Circle, Home, Paperclip } from "lucide-react";
import { WazawanReportingPreview } from "./WazawanReportingPreview";
import { WazawanAssetPreview } from "./WazawanAssetPreview";
import { WazawanFormPreview } from "./WazawanFormPreview";
import { WazawanHistoryPrompt } from "./WazawanHistoryPrompt";
import { WazToolbar } from "./toolbar/WazToolbar";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/features/appData";
import toast, { Toaster } from "react-hot-toast";
import { toastFail } from "../../config";
import {
  apiAIAskAssetQuestion,
  apiAIAskFormFillQuestion,
  apiAIAskOpenQuestion,
  apiAIAskQuestion,
  apiAIAskReportingQuestion,
  apiAIAskUIQuestion,
} from "../../services/AIService";
import { DCToolbar } from "../app/dynamic-components/DCToolbar";
import { DCRouter } from "../app/dynamic-components/DCRouter";
import { apiGetWorkspace, apiGetWorkspaceCollections } from "../../api";
import { NR_DELETE_THIS_EMPLOYEE_RECORD_ID } from "../app/dynamic-components/DCRouter";
import { WazawanFormFillPreview } from "./WazawanFormFillPreview";
import { WazawanHistoryItem } from "./WazawanHistoryItem";
import { OverviewItem, WazawanOverview } from "./overview/WazawanOverview";
import { v4 as uuidv4 } from "uuid";

const HISTORY_ID = "history";

const AVATAR_USER =
  "https://chapter2-public-assets.s3.amazonaws.com/f1463927-af01-408a-a76d-22f27b03606a/avatars/c8ad04a1-b710-443c-9ab7-cf23f76ea5ea.png";
const AVATAR_WINSLOW = "/icons/winslow-ai-logo.svg";

type WazawanProps = {};

export type PromptPair = {
  id: string;
  type: PromptType;
  prompt: string;
  response: string;
};

export type PromptType =
  | "general"
  | "reporting"
  | "asset"
  | "ui"
  | "other"
  | "open"
  | "form fill";
const PROMPT_COLORS = {
  a: "grey",
  r: "blue",
  u: "green",
  o: "purple",
  f: "orange",
  b: "yellow",
};

export const Wazawan = ({}: WazawanProps) => {
  const storeUser = useSelector(selectUser);

  const [promptCount, setPromptCount] = useState<number>(0);
  const [prompt, setPrompt] = useState("");
  const [promptHistory, setPromptHistory] = useState<Array<PromptPair>>([]);
  const [hiddenRouting, setHiddenRouting] = useState<
    keyof typeof PROMPT_COLORS | undefined
  >(undefined);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [expandedPromptPair, setExpandedPromptPair] =
    useState<PromptPair | null>(null);

  const [showOverview, setShowOverview] = useState<boolean>(false);
  const [overviewItems, setOverviewItems] = useState<Map<
    string,
    OverviewItem
  > | null>(null);

  const historyRef = useRef(null);
  const endSpacerRef = useRef(null);

  useEffect(() => {
    document.addEventListener("keydown", eventKeyDown);
    return () => {
      document.removeEventListener("keydown", eventKeyDown);
    };
  }, []);

  useEffect(() => {
    if (historyRef?.current) {
      const historyItemPercentages = getOverviewPositions();
      setOverviewItems(historyItemPercentages);
      console.log("historyItemPercentages: ", historyItemPercentages);
    }
  }, [historyRef, promptHistory]);

  const eventKeyDown = (e: KeyboardEvent) => {
    console.log("Key pressed: ", e.key);

    const cmdOrCtrl = e.metaKey || e.ctrlKey;
    console.log("cmdOrCtrl: ", cmdOrCtrl);

    if (cmdOrCtrl && Object.keys(PROMPT_COLORS).includes(e.key.toLowerCase())) {
      setHiddenRouting(e.key.toLowerCase() as keyof typeof PROMPT_COLORS);
      e.stopPropagation();
      e.preventDefault();
    }

    if (e.key === "Escape") {
      setHiddenRouting(undefined);
    }

    if (cmdOrCtrl && e.key === ".") {
      setShowOverview(!showOverview);
    }
  };

  const getPromptType = (prompt: string): PromptType => {
    if (prompt.toLowerCase().includes("reporting")) return "reporting";
    if (prompt.toLowerCase().includes("get")) return "asset";
    if (prompt.toLowerCase().includes("add")) return "ui";

    return "general";
  };

  const getPromptResponse = (promptType: PromptType): string => {
    switch (promptType) {
      case "general":
        return "I'm not sure what you're asking. Can you please rephrase your question?";
      case "reporting":
        return "Here is your data.";
      case "asset":
        return "Here is your file.";
      case "ui":
        return "Please enter this information.";
      default:
        return "I'm not sure what you're asking. Can you please rephrase your question?";
    }
  };

  // function calculateChildScrollOffset(parent, child) {
  //     // Get the parent and child bounding rectangles
  //     const parentRect = parent.getBoundingClientRect();
  //     const childRect = child.getBoundingClientRect();

  //     // Get the scroll position of the parent
  //     const parentScrollTop = parent.scrollTop;

  //     // Calculate the child’s position relative to the parent, considering scrolling
  //     const childTopRelativeToParent = childRect.top - parentRect.top + parentScrollTop;
  //     const childBottomRelativeToParent = childTopRelativeToParent + childRect.height;

  //     // Get the height of the parent (visible area)
  //     const parentHeight = parentRect.height;

  //     // Calculate the total scrollable height (parent height minus the child height)
  //     const scrollableHeight = parent.scrollHeight - parentHeight;

  //     // Calculate the percentage offset of the child within the parent’s scrollable area
  //     const percentageOffset = ((childTopRelativeToParent / scrollableHeight) * 100);

  //     // Return the calculated percentage offset
  //     return percentageOffset;
  //   }

  const getOverviewItemPercentages = (parent: any, child: any) => {
    // Get the parent and child bounding rectangles
    const parentRect = parent.getBoundingClientRect();
    const childRect = child.getBoundingClientRect();

    // Get the scroll position of the parent
    const parentScrollTop = parent.scrollTop;

    // Calculate the child’s position relative to the parent, considering scrolling
    const childTopRelativeToParent =
      childRect.top - parentRect.top + parentScrollTop;
    const childBottomRelativeToParent =
      childTopRelativeToParent + childRect.height;

    // Get the height of the parent (visible area)
    const parentHeight = parentRect.height;

    // Calculate the total scrollable height (parent height minus the child height)
    const scrollableHeight = parent.scrollHeight - parentHeight;

    // Calculate the percentage offset of the child within the parent’s scrollable area
    const percentageOffset =
      (childTopRelativeToParent / scrollableHeight) * 100;

    // Return the calculated percentage offset
    return {
      topPercentage: percentageOffset,
      bottomPercentage:
        percentageOffset + (childRect.height / scrollableHeight) * 100,
    };
  };

  const getOverviewItemPercentages2 = (parent: any, child: any) => {
    const parentRect = parent.getBoundingClientRect();
    const childRect = child.getBoundingClientRect();

    const childTopRelativeToParent = child.offsetTop;
    const childBottomRelativeToParent =
      childTopRelativeToParent + childRect.height;
    const parentHeight = parent.scrollHeight;

    // Calculate the percentage of the child within the parent
    const topPercentage = (childTopRelativeToParent / parentHeight) * 100;
    const bottomPercentage = (childBottomRelativeToParent / parentHeight) * 100;

    return { topPercentage, bottomPercentage };
  };

  const getOverviewPositions = (): Map<string, OverviewItem> | null => {
    const parent = document.getElementById(HISTORY_ID);

    if (!parent) {
      console.error("No history parent");
      return null;
    }

    const finalMap: Map<string, OverviewItem> = new Map();

    const children = parent.children; // Get all children of the parent
    // const divs = Array.from(children).filter(child => child.tagName === 'DIV'); // Filter for divs only

    // const parentHeight = parent.clientHeight;
    const parentHeight = parent.scrollHeight;

    console.log("parentHeight: ", parentHeight);

    Array.from(children).forEach((child) => {
      const rect = child.getBoundingClientRect();

      // const childTop = rect.top - parent.getBoundingClientRect().top; // Position relative to the parent
      // const childHeight = rect.height;
      // const childBottom = childTop + childHeight;

      // console.log("childTop: ", childTop);
      // console.log("childHeight: ", childHeight);
      // console.log("childBottom: ", childBottom);

      // // Calculate percentage based on the parent's height
      // const topPercentage = (childTop / parentHeight) * 100;
      // const bottomPercentage = (childBottom / parentHeight) * 100;

      const { topPercentage, bottomPercentage } = getOverviewItemPercentages2(
        parent,
        child
      );
      console.log("child id: ", child.id);
      console.log("topPercentage: ", topPercentage);
      console.log("bottomPercentage: ", bottomPercentage);

      const promptPair = promptHistory.find((pair) => pair.id === child.id);
      if (!promptPair) {
        console.error("No prompt pair found for id: ", child.id);
        return;
      }

      finalMap.set(child.id, {
        id: child.id,
        type: promptPair.type,
        topPercentage,
        bottomPercentage,
      } as OverviewItem);
    });

    return finalMap;
  };

  const handlePromptSubmit = async (thePrompt: string) => {
    if (!storeUser?.currentWorkspaceId) {
      console.error("No current workspace id");
      return;
    }

    console.log("Prompt submitted: ", thePrompt);

    const newId = uuidv4();

    // const promptType : PromptType = getPromptType(thePrompt);
    // const promptResponse = getPromptResponse(promptType);

    // const newPromptPair : PromptPair = {
    //     type: promptType,
    //     prompt: thePrompt,
    //     response: promptResponse
    // }

    // setPromptHistory([...promptHistory, newPromptPair]);
    // setPromptCount(promptCount + 1);

    if (hiddenRouting === "r" || thePrompt.trim().substring(0, 3) === "/r ") {
      const finalPrompt =
        hiddenRouting === "r"
          ? thePrompt.trim()
          : thePrompt.trim().substring(3);
      console.log("[Wazawan] reporting question: ", finalPrompt);

      const response = await apiAIAskReportingQuestion(
        storeUser.currentWorkspaceId,
        finalPrompt
      );
      console.log("[Wazawan] reporting response: ", response);

      if (!response) {
        console.error("Failed to get reporting response");
        toast.error("Failed to get reporting response", toastFail);
        return;
      }

      const newPromptPair: PromptPair = {
        id: newId,
        type: "reporting",
        prompt: thePrompt,
        response: response,
      };

      setPromptHistory([...promptHistory, newPromptPair]);
      setPromptCount(promptCount + 1);
      showLastResponse();

      return;
    }

    if (hiddenRouting === "u" || thePrompt.trim().substring(0, 3) === "/u ") {
      const finalPrompt =
        hiddenRouting === "u"
          ? thePrompt.trim()
          : thePrompt.trim().substring(3);
      console.log("[Wazawan] ui question: ", finalPrompt);

      const response = await apiAIAskUIQuestion(
        storeUser.currentWorkspaceId,
        finalPrompt
      );
      console.log("[Wazawan] UI response: ", response);

      if (!response) {
        console.error("Failed to get UI response");
        toast.error("Failed to get UI response", toastFail);
        return;
      }

      const newPromptPair: PromptPair = {
        id: newId,
        type: "ui",
        prompt: thePrompt,
        response: response,
      };

      setPromptHistory([...promptHistory, newPromptPair]);
      setPromptCount(promptCount + 1);
      showLastResponse();

      return;
    }

    if (hiddenRouting === "b" || thePrompt.trim().substring(0, 3) === "/b ") {
      const finalPrompt =
        hiddenRouting === "b"
          ? thePrompt.trim()
          : thePrompt.trim().substring(3);
      console.log("[Wazawan] Q&A question: ", finalPrompt);

      const collections = await apiGetWorkspaceCollections(
        storeUser.currentWorkspaceId
      );

      if (!collections) {
        console.error("Failed to get workspace");
        toast.error("Failed to get workspace", toastFail);
        return;
      }

      const response = await apiAIAskQuestion({
        collectionId: collections[0].id,
        question: finalPrompt,
        userId: storeUser.id,
        askAsEmployeeId: undefined,
        askAsPersonaId: undefined,
      });

      console.log("[Wazawan] Q&A response: ", response);

      if (!response) {
        console.error("Failed to get Q&A response");
        toast.error("Failed to get Q&A response", toastFail);
        return;
      }

      const newPromptPair: PromptPair = {
        id: newId,
        type: "general",
        prompt: thePrompt,
        response: JSON.stringify(response),
      };

      setPromptHistory([...promptHistory, newPromptPair]);
      setPromptCount(promptCount + 1);
      showLastResponse();

      return;
    }

    if (hiddenRouting === "a" || thePrompt.trim().substring(0, 3) === "/a ") {
      const finalPrompt =
        hiddenRouting === "a"
          ? thePrompt.trim()
          : thePrompt.trim().substring(3);
      console.log("[Wazawan] asset question: ", finalPrompt);

      const response = await apiAIAskAssetQuestion(
        storeUser.currentWorkspaceId,
        NR_DELETE_THIS_EMPLOYEE_RECORD_ID,
        finalPrompt
      );
      console.log("[Wazawan] asset response: ", response);

      if (!response) {
        console.error("Failed to get asset response");
        toast.error("Failed to get asset response", toastFail);
        return;
      }

      let updatedResponse: any = {};

      try {
        updatedResponse = JSON.parse(response);
      } catch (e) {
        updatedResponse = response;
      }
      // const updatedResponse = JSON.parse(response);

      if (typeof updatedResponse !== "object") {
        console.error("Asset response is not an object");
        toast.error("Asset response is not an object", toastFail);
        return;
      }

      updatedResponse.object = "asset";

      const newPromptPair: PromptPair = {
        id: newId,
        type: "asset",
        prompt: thePrompt,
        response: JSON.stringify(updatedResponse),
      };

      setPromptHistory([...promptHistory, newPromptPair]);
      setPromptCount(promptCount + 1);
      showLastResponse();

      return;
    }

    if (hiddenRouting === "o" || thePrompt.trim().substring(0, 3) === "/o ") {
      const finalPrompt =
        hiddenRouting === "o"
          ? thePrompt.trim()
          : thePrompt.trim().substring(3);
      console.log("[Wazawan] open question: ", finalPrompt);
      const response = await apiAIAskOpenQuestion(
        storeUser.currentWorkspaceId,
        finalPrompt
      );
      console.log("[Wazawan] open response: ", response);

      if (!response) {
        console.error("Failed to get open response");
        toast.error("Failed to get open response", toastFail);
        return;
      }

      const newPromptPair: PromptPair = {
        id: newId,
        type: "open",
        prompt: thePrompt,
        response,
      };

      setPromptHistory([...promptHistory, newPromptPair]);
      setPromptCount(promptCount + 1);
      showLastResponse();

      return;
    }

    if (hiddenRouting === "f" || thePrompt.trim().substring(0, 3) === "/f ") {
      const finalPrompt =
        hiddenRouting === "f"
          ? thePrompt.trim()
          : thePrompt.trim().substring(3);
      console.log("[Wazawan] form fille question: ", finalPrompt);
      const response = await apiAIAskFormFillQuestion(
        storeUser.currentWorkspaceId,
        finalPrompt
      );
      console.log("[Wazawan] form fill response: ", response);

      if (!response) {
        console.error("Failed to get form fill response");
        toast.error("Failed to get form fill response", toastFail);
        return;
      }

      const newPromptPair: PromptPair = {
        id: newId,
        type: "form fill",
        prompt: thePrompt,
        response: JSON.stringify(response),
      };

      setPromptHistory([...promptHistory, newPromptPair]);
      setPromptCount(promptCount + 1);
      showLastResponse();

      return;
    }
  };

  const handleOverviewClick = (id: string) => {
    scrollToHistoryItem(id);
  };

  const showLastResponse = () => {
    if (endSpacerRef.current) {
      const current = endSpacerRef.current as any;
      current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToHistoryItem = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // if (historyRef?.current) {
  //     const historyItemPercentages = getHistoryItemPercentages();
  //     setOverviewItems(historyItemPercentages);
  //     console.log("historyItemPercentages: ", historyItemPercentages);
  // }

  console.log("hiddenRouting: ", hiddenRouting);
  console.log("historyRef: ", historyRef);
  console.log("showOverview: ", showOverview);

  return (
    <div className="wazawan">
      <WazToolbar />
      {promptHistory.length === 0 && (
        <div className="wazawan-home">
          <div className="wazawan-home-title">Hi, Niel</div>
          <div className="wazawan-home-prompt">
            <input
              type="text"
              value={prompt}
              placeholder="What can I help you with?"
              autoFocus={true}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handlePromptSubmit(prompt);
                }
              }}
            />
            <div className="wazawan-home-prompt-actions">
              <Paperclip size={16} color="lightgrey" />
            </div>
          </div>
        </div>
      )}

      {promptHistory.length > 0 && isExpanded && expandedPromptPair && (
        <div className="wazawan-expander-container">
          <div className="wazawan-expander">
            <DCToolbar
              promptPair={expandedPromptPair}
              onClose={() => {
                setIsExpanded(false);
                setExpandedPromptPair(null);
              }}
            />
            <div className="wazawan-dc">
              <DCRouter
                promptPair={expandedPromptPair}
                onCancel={() => {
                  setIsExpanded(false);
                  setExpandedPromptPair(null);
                }}
                onClose={() => {
                  setIsExpanded(false);
                  setExpandedPromptPair(null);
                }}
                onSubmit={() => {
                  setIsExpanded(false);
                  setExpandedPromptPair(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {promptHistory.length > 0 && !isExpanded && (
        <div className="wazawan-history">
          <div
            id={HISTORY_ID}
            ref={historyRef}
            className="wazawan-history-items">
            {promptHistory.map((promptPair, index) => {
              return (
                <WazawanHistoryItem
                  key={index}
                  id={index.toString()}
                  promptPair={promptPair}
                  onExpand={(promptPair) => {
                    setIsExpanded(true);
                    setExpandedPromptPair(promptPair);
                  }}
                />
              );
            })}

            <div className="wazawav-history-bottom-spacer" ref={endSpacerRef}>
              {" "}
            </div>
          </div>

          <div className="wazawan-history-prompt">
            <div className="wazawan-history-prompt-container">
              <input
                type="text"
                value={prompt}
                placeholder="What can I help you with?"
                autoFocus={true}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handlePromptSubmit(prompt);
                  }
                }}
              />
              <div className="wazawan-home-prompt-actions">
                <Paperclip size={16} color="lightgrey" />
              </div>
            </div>

            {/* <WazawanHistoryPrompt onSubmit={handlePromptSubmit}/> */}
          </div>
        </div>
      )}

      {hiddenRouting && (
        <div className="wazawan-prompt-type">
          <Circle
            size={16}
            color={PROMPT_COLORS[hiddenRouting]}
            fill={PROMPT_COLORS[hiddenRouting]}
          />
        </div>
      )}

      {showOverview && (
        <WazawanOverview
          overviewItems={overviewItems}
          onClick={handleOverviewClick}
        />
      )}

      <Toaster />
    </div>
  );
};
